import React, { useState, useEffect } from 'react';
import './AdminPage.css';
import { useOutletContext, Navigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken, getUserRole } from '../Utils';
import { getPageTitle } from '../constants';
import BadgeItem, { BADGE_TYPES } from '../components/BadgeItem';

interface Badge {
  id: number;
  name: string;
  type: number;
}

interface Stats {
  usersCount: number | null;
  eventsCount: number | null;
}

const AdminPage: React.FC = () => {
  const { translations } = useOutletContext<{ translations: Record<string, string> }>();
  const [stats, setStats] = useState<Stats>({ usersCount: null, eventsCount: null });
  const [badges, setBadges] = useState<Badge[]>([]);
  const [newBadgeName, setNewBadgeName] = useState('');
  const [newBadgeType, setNewBadgeType] = useState(0);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const isAdmin = getUserRole() === 'admin';

  useEffect(() => {
    document.title = getPageTitle('Administration');
  }, []);

  const getConfig = () => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return { headers, withCredentials: true };
  };

  useEffect(() => {
    const loadStats = async () => {
      const config = getConfig();
      try {
        const [usersRes, eventsRes] = await Promise.allSettled([
          axios.get('/api/users/count', config),
          axios.get('/api/events/count', config),
        ]);
        setStats({
          usersCount: usersRes.status === 'fulfilled' ? usersRes.value.data : null,
          eventsCount: eventsRes.status === 'fulfilled' ? eventsRes.value.data : null,
        });
      } catch (err) {
        console.error('Failed to load stats:', err);
      }
    };

    const loadBadges = async () => {
      try {
        const response = await axios.get('/api/badges', getConfig());
        setBadges(response.data);
      } catch (err) {
        console.error('Failed to load badges:', err);
      }
    };

    loadStats();
    loadBadges();
  }, []);

  const handleSaveBadge = async () => {
    if (!newBadgeName.trim()) return;
    try {
      await axios.post('/api/badges', { name: newBadgeName, type: newBadgeType }, {
        ...getConfig(),
        headers: { ...getConfig().headers, 'Content-Type': 'application/json' },
      });
      setNewBadgeName('');
      setMessage({ text: 'Badge saved', error: false });
      const response = await axios.get('/api/badges', getConfig());
      setBadges(response.data);
    } catch (err: any) {
      setMessage({ text: `${err.response?.status || 'Error'}: ${err.response?.data || err.message}`, error: true });
    }
  };

  const handleDeleteBadge = async (id: number) => {
    try {
      await axios.delete(`/api/badges/${id}`, getConfig());
      setBadges(badges.filter(b => b.id !== id));
    } catch (err: any) {
      setMessage({ text: `Failed to delete: ${err.response?.status || err.message}`, error: true });
    }
  };

  const handleTestEmail = async () => {
    try {
      const response = await axios.post('/api/health/email/test', null, getConfig());
      setMessage({ text: JSON.stringify(response.data), error: false });
    } catch (err: any) {
      setMessage({ text: `${err.response?.status || 'Error'}: ${err.response?.data || err.message}`, error: true });
    }
  };

  if (!isAdmin) {
    return <Navigate to="/diary" replace />;
  }

  return (
    <div className="admin-page">
      <h2>{translations["message.nav.administration"]}</h2>

      {message && (
        <div className={`admin-message ${message.error ? 'admin-message-error' : 'admin-message-success'}`}>
          {message.text}
          <button className="admin-message-close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="admin-grid">
        <div className="admin-section">
          <h3>{translations["title.statistic"]}</h3>
          <table className="admin-stats-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total users registered</td>
                <td>{stats.usersCount ?? '—'}</td>
              </tr>
              <tr>
                <td>Total events created</td>
                <td>{stats.eventsCount ?? '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="admin-section">
          <h3>{translations["nav.links"]}</h3>
          <div className="admin-links">
            <a href="/users" className="admin-link">Users</a>
            <button className="admin-button" onClick={handleTestEmail}>Test email scheduling</button>
          </div>
        </div>
      </div>

      <div className="admin-section">
        <h3>{translations["message.badges"]}</h3>
        <div className="badge-legend">
          {Object.entries(BADGE_TYPES).map(([code, { label, className }]) => (
            <span key={code} className={`badge-legend-item ${className}`}>{label}</span>
          ))}
        </div>
        <div className="admin-badges-grid">
          <div className="badges-list">
            {badges.map((badge) => (
              <BadgeItem
                key={badge.id}
                name={badge.name}
                type={badge.type}
                onAction={() => handleDeleteBadge(badge.id)}
              />
            ))}
          </div>
          <div className="badge-form">
            <input
              className="search-input"
              placeholder={translations["object.name"]}
              value={newBadgeName}
              onChange={(e) => setNewBadgeName(e.target.value)}
            />
            <select
              className="form-select"
              value={newBadgeType}
              onChange={(e) => setNewBadgeType(Number(e.target.value))}
            >
              {Object.entries(BADGE_TYPES).map(([code, { label }]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
            <button className="search-button" onClick={handleSaveBadge}>
              {translations["button.save"]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;